import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom } from '@blaze-runtime/engine-domain';

import { type DefinitionMapper } from '../../../ai';
import { NumberType } from '../../../ai/models/chart';

import { type BaseChartDefinition, type ChartType } from './chart.definition';

abstract class BaseChartDefinitionMapper<T extends ChartType> implements DefinitionMapper<T> {
  abstract readonly type: T;

  extract(element: CanvasElementFrom<T>): PartialDeep<BaseChartDefinition<T>> {
    const content = element.props.content;

    return {
      title: content?.title?.label,
      showTitle: content?.title?.enabled,
      xAxisLabel: content?.chart?.xDescription,
      yAxisLabel: content?.chart?.yDescription,
      yAxisFormat: this.getAxisFormat(element),
    };
  }

  protected getAxisFormat(element: CanvasElementFrom<T>): NumberType | undefined {
    switch (element.props.content?.format?.type) {
      case 'Currency':
        return NumberType.Currency;
      case 'Percent':
        return NumberType.Percent;
      case 'Number':
        return NumberType.Number;
      default:
        return undefined;
    }
  }
}

export class BarChartDefinitionMapper extends BaseChartDefinitionMapper<'chartBar'> {
  readonly type = 'chartBar';
}

export class LineChartDefinitionMapper extends BaseChartDefinitionMapper<'chartLine'> {
  readonly type = 'chartLine';
}

export class DotChartDefinitionMapper extends BaseChartDefinitionMapper<'chartDot'> {
  readonly type = 'chartDot';
}

export class ColumnChartDefinitionMapper extends BaseChartDefinitionMapper<'chartColumn'> {
  readonly type = 'chartColumn';
}
